import Cookies from "js-cookie";
import { TokenRefreshService } from "./TokenRefreshService";
import {
  ACCESS_TOKEN_KEY,
  ADMIN_SESSION_KEY,
  REFRESH_TOKEN_ID_KEY,
  clearStoredTokens,
  isJwtShaped,
  readStoredTokens,
  writeStoredTokens
} from "./tokenStorage";

interface FetchInit extends RequestInit {
  headers?: Record<string, string>;
}

interface ApiError extends Error {
  status: number;
  data?: unknown;
}

// Frozen so a same-origin script can't swap out clearAuth/setTokens to suppress
// logout or exfiltrate tokens on every future refresh.
// Delegates the localStorage half to tokenStorage so this client and ApiProvider cannot drift
// apart again - they were two implementations of the same store, which is what let an embed-led
// failure reach the admin's real credentials through here (AB#10738).
//
// The COOKIE writes stay local on purpose. tokenStorage writes localStorage only, and moving these
// into it would be a behaviour change to every admin session riding along a bug fix. The surface that
// used to depend on the cookie being refreshed on the adoption path (MeetingAgenda.js, which read it
// with no localStorage fallback) now goes through this client instead (AB#10862). Other hand-rolled
// bearer readers remain - Project/ProjectShared.js and ProjectImport/project-import-api.js - and are
// filed separately.
const TOKEN_CALLBACKS = Object.freeze({
  // ADMIN_SESSION_KEY is correct ONLY because this client is imported by admin surfaces. It is a
  // module-level singleton, so an embed or public-dashboard bundle importing it would join the admin
  // flight and, on session death, run clearStoredTokens() against the admin's real credentials -
  // recreating AB#10738 exactly. An embed must go through ApiProvider, which keys per instance.
  sessionKey: ADMIN_SESSION_KEY,
  // Via tokenStorage so a stored empty string reads as null - "worth asking" - rather than as the
  // never-refresh sentinel, which used to turn an empty cookie into a hard sign-out.
  getRefreshTokenId: () => readStoredTokens().refreshTokenId,
  setTokens: (accessToken: string, refreshTokenId: string) => {
    if (!writeStoredTokens(accessToken, refreshTokenId)) {
      // Kept at this exact wording: it is the message the poisoned-token guard has always logged
      // and the one coreApiClient.test.mjs asserts on (AB#10507).
      console.error("Refusing an access token that is not a JWT (AB#10507)");
      return;
    }

    Cookies.set(ACCESS_TOKEN_KEY, accessToken);
    if (refreshTokenId) {
      Cookies.set(REFRESH_TOKEN_ID_KEY, refreshTokenId);
    }
  },
  clearAuth: () => {
    // A straight swap: clearStoredTokens does the same bare removes plus the same localStorage
    // deletes this used to do inline.
    clearStoredTokens();
  }
});

// Only a usable token is reported. A stored non-JWT is skipped rather than returned, so this
// client sends an anonymous request the 401 path can recover from instead of `Bearer undefined`,
// which the API can only reject (AB#10507).
function readStoredToken(): string | undefined {
  // Via tokenStorage, which additionally purges a poisoned value on read rather than just stepping
  // over it - so a browser that stored "undefined" heals instead of carrying it until someone
  // clears site data by hand (AB#10507).
  const stored = readStoredTokens().accessToken;
  return isJwtShaped(stored) ? stored : undefined;
}

// True only when the JWT is already expired. Returns false for a
// missing/unparseable token or one without an exp claim — those fall through to
// the reactive 401-refresh path rather than forcing a refresh.
//
// We deliberately trigger the proactive refresh ONLY on an already-dead token,
// not on a "near expiry" window: a token still valid at send-time is accepted
// by the server when the request is received, and proactively refreshing a
// still-valid token would risk discarding it — TokenRefreshService.clearAuth()
// runs on any refresh failure, so a transient blip could wipe a good token and
// turn a would-succeed request into a 401. See AB#9492.
function isTokenExpired(token: string | undefined): boolean {
  if (!token) return false;
  const parts = token.split(".");
  if (parts.length !== 3) return false;
  try {
    // Browser-only bundle: atob tolerates the unpadded base64url payload after
    // swapping the URL-safe chars back to standard base64.
    const json = decodeURIComponent(
      atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"))
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    const payload = JSON.parse(json) as { exp?: number };
    if (typeof payload.exp !== "number") return false;
    return payload.exp * 1000 <= Date.now();
  } catch (_) {
    return false;
  }
}

class CoreApiClient {
  private _getBase(): string {
    return (window._JSCONFIG && window._JSCONFIG.ReactAppApiCoreBaseUrl) || "";
  }

  private _getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {};
    // Shares readStoredToken's guard so the pre-send expiry check and the header can never
    // diverge as they are edited apart, and neither can emit a non-JWT.
    const token = readStoredToken();
    const tenantId = sessionStorage.getItem("_tenantId") || Cookies.get("_cid");
    if (token) headers["Authorization"] = `Bearer ${token}`;
    if (tenantId) headers["X-Tenant-ID"] = tenantId;
    return headers;
  }

  private async _fetch(
    url: string,
    init: FetchInit = {},
    isRetry = false
  ): Promise<Response> {
    // Proactive refresh: if the stored token is already expired, renew it
    // BEFORE sending so the request doesn't 401 — notably a large agenda-import
    // upload that would otherwise have to re-send its whole body after a
    // reactive refresh. Reuses the single-flighted TokenRefreshService. Skipped
    // on the retry pass (token was just refreshed). Because it only fires on an
    // already-dead token, a failed refresh never discards a still-valid token;
    // the reactive 401 path below remains the backstop. See AB#9492.
    let proactiveRefreshFailed = false;
    if (!isRetry) {
      const tokenBeforeSend = readStoredToken();
      if (isTokenExpired(tokenBeforeSend)) {
        try {
          await TokenRefreshService.refreshToken(
            TOKEN_CALLBACKS,
            tokenBeforeSend,
            "coreapi-proactive"
          );
        } catch (_) {
          // Fall through and send the request anyway; the reactive path below
          // won't re-attempt a refresh we already know just failed.
          proactiveRefreshFailed = true;
        }
      }
    }

    const { headers: extraHeaders, ...rest } = init;
    const ownHeaders = this._getHeaders();
    const response = await fetch(`${this._getBase()}${url}`, {
      ...rest,
      headers: { ...ownHeaders, ...(extraHeaders || {}) }
    });

    if (response.status === 403 && !isRetry) {
      return response;
    }

    if (response.status === 401 && !isRetry && !proactiveRefreshFailed) {
      try {
        const sentToken = ownHeaders["Authorization"]?.replace(/^Bearer /, "");
        await TokenRefreshService.refreshToken(
          TOKEN_CALLBACKS,
          sentToken,
          "coreapi-401"
        );
      } catch (err) {
        return response;
      }
      return this._fetch(url, init, true);
    }

    return response;
  }

  private async _fetchJson<T = unknown>(
    url: string,
    init: FetchInit = {}
  ): Promise<T> {
    const response = await this._fetch(url, init);
    if (!response.ok) {
      const err = new Error(`HTTP ${response.status}`) as ApiError;
      err.status = response.status;
      try {
        err.data = await response.json();
      } catch (_) {}
      throw err;
    }
    // 204 No Content (and any empty body) is a success with nothing to parse —
    // calling response.json() on it would throw. Core API command endpoints that
    // return a non-generic Result land here.
    if (response.status === 204) {
      return undefined as unknown as T;
    }
    const text = await response.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  async get<T = unknown>(url: string, opts: FetchInit = {}): Promise<T> {
    return this._fetchJson<T>(url, { ...opts, method: "GET" });
  }

  async post<T = unknown>(
    url: string,
    body: unknown,
    opts: FetchInit = {}
  ): Promise<T> {
    return this._fetchJson<T>(url, {
      ...opts,
      method: "POST",
      headers: { "Content-Type": "application/json", ...(opts.headers || {}) },
      body: JSON.stringify(body)
    });
  }

  async put<T = unknown>(
    url: string,
    body: unknown,
    opts: FetchInit = {}
  ): Promise<T> {
    return this._fetchJson<T>(url, {
      ...opts,
      method: "PUT",
      headers: { "Content-Type": "application/json", ...(opts.headers || {}) },
      body: JSON.stringify(body)
    });
  }

  // `body` is optional because both shapes exist behind this client: Core API command
  // endpoints that take a [FromBody] request DTO on DELETE (DocumentsController.
  // DeleteDocument), and plain DELETE /resource/{id} routes that take none. Omitting it
  // sends no body and no Content-Type, which is what a bodyless DELETE expects.
  async delete<T = unknown>(
    url: string,
    body?: unknown,
    opts: FetchInit = {}
  ): Promise<T> {
    return this._fetchJson<T>(url, {
      ...opts,
      method: "DELETE",
      ...(body === undefined
        ? {}
        : {
            headers: {
              "Content-Type": "application/json",
              ...(opts.headers || {})
            },
            body: JSON.stringify(body)
          })
    });
  }

  async upload<T = unknown>(
    url: string,
    formData: FormData,
    opts: FetchInit = {}
  ): Promise<T> {
    return this._fetchJson<T>(url, {
      ...opts,
      method: "POST",
      body: formData
      // No Content-Type header — browser sets multipart/form-data with boundary
    });
  }

  async download(url: string, opts: FetchInit = {}): Promise<Blob> {
    const response = await this._fetch(url, { ...opts, method: "GET" });
    if (!response.ok) {
      const err = new Error(`HTTP ${response.status}`) as ApiError;
      err.status = response.status;
      throw err;
    }
    return response.blob();
  }
}

export const coreApiClient = new CoreApiClient();
